import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "@core/auth/auth.service";
import { DataFilter } from "@core/components/dynamic-table/dynamic-table.component";
import { Company } from "@core/models/company";
import { StatusPipe } from "@core/pipes/tabs/status.pipe";
import { MessageService } from "primeng/api";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { ApiResponse, BaseApiService } from "src/app/api/base";
import { PagerData } from "src/app/services/companies-search/saved-search.service";
import { TimeAgoPipe } from "../pipes/time-ago.pipe";
import moment from "moment";
import { getActiveOrLastICVotingWorkflow } from "src/app/utils/company-utils";
import { Nullable } from "primeng/ts-helpers";
import { UserRolesService } from "src/app/services/user-roles/user-roles.service";
import { UserGroupsService } from "src/app/services/user-groups/user-groups.service";
import { Investor } from "@core/models/auxiliary-company-models";
import { CONVERT_TO_ARRAY_ITEMS } from "./filter-helper.service";
import { SocialLinksPipe } from "@core/pipes/social-links.pipe";
import { AnalyticsWidgetService } from "src/app/services/analytics-widget/analytics-widget.service";

interface DateDiff {
  diff: number;
  ms: number;
  s: number;
  m: number;
  h: number;
  d: number;
}

@Injectable({
  providedIn: "root",
})
export class CompanyV2Service extends BaseApiService<Company> {
  public searchQueryChanges$ = new Subject<string>();

  constructor(
    authService: AuthService,
    router: Router,
    messageService: MessageService,
    private statusPipe: StatusPipe,
    private timeAgoPipe: TimeAgoPipe,
    private socialLinksPipe: SocialLinksPipe,
    public userRolesService: UserRolesService,
    public userGroupsService: UserGroupsService,
  ) {
    super("/", authService, router, messageService);
  }

  transformBooleanAndNonArray(filterParams: any): any {
    let transformedFilterParams = { ...filterParams };
    if (transformedFilterParams && transformedFilterParams.filtersString?.length > 0) {
      const keywordFilters = JSON.parse(transformedFilterParams.filtersString);
      delete transformedFilterParams.filtersString;
      transformedFilterParams.keywordsFilter = {};
      if (keywordFilters.length > 0) {
        transformedFilterParams.keywordsFilter.keywords = keywordFilters;
      }
    }
    return this.covertBoolAndStrings(transformedFilterParams);
  }

  covertBoolAndStrings(filterParams: any): any {
    for (const key in filterParams) {
      if (key === "keywordsFilter") {
        continue;
      }
      if (Array.isArray(filterParams[key]) && filterParams[key].length > 0) {
        filterParams[key] = this.covertBoolAndStrings(filterParams[key]);
      }
      if (!Array.isArray(filterParams[key]) && typeof filterParams[key] === "object" && filterParams[key] !== null) {
        filterParams[key] = this.covertBoolAndStrings(filterParams[key]);
      } else if (!isNaN(filterParams[key])) {
        filterParams[key] = Number(filterParams[key]);
      } else if (filterParams[key] === "false") {
        filterParams[key] = false;
      } else if (filterParams[key] === "true") {
        filterParams[key] = true;
      }
      if (!!CONVERT_TO_ARRAY_ITEMS[key]) {
        if (typeof filterParams[key] === "string" || typeof filterParams[key] === "number") {
          filterParams[key] = [filterParams[key]];
        }
      }
    }
    return filterParams;
  }

  getCompaniesV2(): Observable<ApiResponse<Company>> {
    return this.get(`companies/search/active`);
  }

  getActiveCompaniesV2(filter: DataFilter): Observable<ApiResponse<PagerData<Company>>> {
    // Convert filter.assigneeRole from string to number if it exists
    const transformFilter: any = filter;
    if (filter.assigneeRole) {
      transformFilter.assigneeRole = +filter.assigneeRole; // Use unary `+` operator to convert string to number
    }
    const transformedFilter = this.transformBooleanAndNonArray(filter);
    return this.post("companies/search/active", null, transformedFilter);
  }

  transformCompanyData(companies: Company[]) {
    return companies.map((company) => {
      const stage = this.statusPipe.transform(company.statusId);
      const coverageUsers = company.coverageUsers.map((item) => item.name).join("\n");
      const investors = company.companyInvestors;
      const investorsList = investors && investors.length > 0 
      ? investors.map((item: Investor) => item.name).join("\n") 
      : "-";
        
      return {
        ...company,
        id: company.id,
        title: company.name,
        subtitle: `${company.address?.city} | ${stage}`,
        description: company.description,
        details: [
          { title: "Financials", value: company.targetFinancials },
          { title: "CEO Location", value: company.ceoFounderCity || "-" },
          { title: "Coverage Users", value: coverageUsers },
        ],
        route: `/companies/details/${company.id}`,
        nameColumn: {
          name: company.name,
          city: company.address?.city,
          country: company.address?.country?.name,
          description: company.description,
          route: `/companies/details/${company.id}`,
          socialLinks: this.socialLinksPipe.transform(company),
        },
        investorsDetails: company.investorsDetails,
        investorsList: investorsList ?? "-",
        investors: investors,
        coverageUsersList: coverageUsers,
        stage,
      };
    });
  }

  transformInteractionData(companies: Company[]) {
    return companies.map((company) => {
      const stage = this.statusPipe.transform(company.statusId);
      const lastInteraction = this.timeAgoPipe.transform(company.latestInteraction?.interactionDate || "");
      const coverageUsers = company.coverageUsers.map((item) => item.name).join("\n");

      return {
        ...company,
        id: company.id,
        title: company.name,
        subtitle: `${company.address?.city} | ${stage}`,
        description: company.description,
        details: [
          { title: "Last Interaction", value: lastInteraction },
          { title: "Coverage Users", value: coverageUsers },
        ],
        route: `/companies/details/${company.id}`,
        nameColumn: {
          name: company.name,
          city: company.address?.city,
          country: company.address?.country?.name,
          description: company.description,
          socialLinks: this.socialLinksPipe.transform(company),
        },
        coverageUsersList: coverageUsers,
        stage,
        lastInteraction,
      };
    });
  }

  transformElevationsData(companies: Company[], breakIns: number[] = []) {
    return companies.map((company) => {
      const coverageUsers = company.coverageUsers.map((item) => item.name).join("\n");
      const stage = this.statusPipe.transform(company.statusId);
      const investors = company.companyInvestors;
      const investorsList = investors && investors.length > 0 
        ? investors.map((item: Investor) => item.name).join("\n") 
        : "-";

      return {
        ...company,
        id: company.id,
        title: company.name,
        subtitle: `${company.address?.city} | ${stage}`,
        description: company.description,
        details: [
          { title: "Elevation Owner", value: company.elevationOwner?.name},
          { title: "Outreach Owner", value: company.outreachOwner?.name},
          { title: "HC / 1Y / 2Y", value: company.hC1Y2Y ?? "" },
          { title: "PIC", value: company.totalPIC ?? "" },
          { title: "Investors", value: investorsList},
          { title: "Financials", value: company.targetFinancials },
          { title: "Next Steps", value: company.nextStep },
        ],
        route: `/companies/details/${company.id}`,
        nameColumn: {
          name: company.name,
          city: company.address?.city,
          description: company.description,
          route: `/companies/details/${company.id}`,
          socialLinks: this.socialLinksPipe.transform(company),
        },
        coverageUsersList: coverageUsers,
        investorsDetails: company.investorsDetails,
        investorsList: investorsList ?? "-",
        investors: investors,
        targetFinancialsColumn: company.targetFinancials?.trim() ? company.targetFinancials : "-",
        outreachOwnerName: company.outreachOwner?.name?.trim() ? company.outreachOwner.name : "-",
        elevationOwnerName: company.elevationOwner?.name?.trim() ? company.elevationOwner.name : "-",
        nextStepColumn: company.nextStep?.trim() ? company.nextStep : "-",
        hC1Y2YColumn: company.hC1Y2Y?.trim() ? company.hC1Y2Y : "-",
        totalPICColumn: company.totalPIC?.trim() ? company.totalPIC : "-",
        breakInColumn: breakIns?.includes(company.id) ? true : false,
      };
    });
  }

  transformAutoElevationsData(companies: Company[]) {
    return companies.map((company) => {
      const coverageUsers = company.coverageUsers.map((item) => item.name).join("\n");
      const stage = this.statusPipe.transform(company.statusId);

      return {
        ...company,
        id: company.id,
        title: company.name,
        subtitle: `${company.address?.city} | ${stage}`,
        description: company.description,
        details: [
          { title: "Coverage Users", value: coverageUsers },
          { title: "Stage", value: stage},
          { title: "Days Since Last Interaction", value: company.daysSinceLastInteraction},
          { title: "# Of Emails Sent", value: company.numberOfEmailsSent}
        ],
        route: `/companies/details/${company.id}`,
        nameColumn: {
          name: company.name,
          city: company.address?.city,
          description: company.description,
          route: `/companies/details/${company.id}`,
          socialLinks: this.socialLinksPipe.transform(company),
        },
        coverageUsersList: coverageUsers,
        // investorsDetails: company.investorsDetails,
        // investors: company.investorsDetails?? "-",
        // targetFinancials: company.targetFinancials,
        // outreachOwnerName : company.outreachOwner?.name,
        // nextStep: company.nextStep,
        lastInteraction: `${company.daysSinceLastInteraction}`,
        emailNumber: `${company.numberOfEmailsSent}`,
        stage,
      };
    });
  }

  transformActiveICData(companies: Company[]) {
    return companies.map((company) => {
      const workflow = getActiveOrLastICVotingWorkflow(company.workflows);
      const dealTeam = workflow?.companyWorkflowUsers?.map((item) => item.user.name).join("\n");
      return {
        ...company,
        id: company.id,
        title: company.name,
        description: company.description,
        details: [
          { title: "Check Size", value: workflow?.checkSize },
          { title: "IC Ask", value: workflow?.description},
          { title: "Deal Team", value: dealTeam! },
          { title: "IC Date", value: moment(workflow?.votingDate).format('MMM DD, YYYY') },
          { title: "Comments", value: workflow?.companyWorkflowComments?.length, openLink: "comments" },
          { title: "Voting", value: "", openLink: "stats" },
        ],
        route: `/companies/details/${company.id}`,
        nameColumn: {
          name: company.name,
          city: company.address?.city,
          description: company.description,
          route: `/companies/details/${company.id}`,
          socialLinks: this.socialLinksPipe.transform(company),
        },
        checkSize: workflow?.checkSize,
        icAsk: workflow?.description,
        dealTeam: dealTeam!,
        createDate: moment(workflow?.votingDate).format('MMM DD, YYYY'),
        companyWorkflowCommentsLength : workflow?.companyWorkflowComments?.length,
        workflow: workflow
      };
    });
  }

  transformArchiveICData(companies: Company[]) {
    return companies.map((company) => {
      const workflow = getActiveOrLastICVotingWorkflow(company.workflows);
      const dealTeam = workflow?.companyWorkflowUsers?.map((item) => item.user.name).join("\n");
      return {
        ...company,
        id: company.id,
        title: company.name,
        description: company.description,
        details: [
          { title: "Check Size", value: workflow?.checkSize },
          { title: "IC Ask", value: workflow?.description},
          { title: "Deal Team", value: dealTeam! },
          { title: "IC Date", value: moment(workflow?.votingDate).format('MMM DD, YYYY') },
          { title: "Comments", value: workflow?.companyWorkflowComments?.length, openLink: "comments" },
          { title: "Summary", value: "", openLink: "summary" },
        ],
        route: `/companies/details/${company.id}`,
        nameColumn: {
          name: company.name,
          city: company.address?.city,
          description: company.description,
          route: `/companies/details/${company.id}`,
          socialLinks: this.socialLinksPipe.transform(company),
        },
        checkSize: workflow?.checkSize,
        icAsk: workflow?.description,
        dealTeam: dealTeam!,
        createDate: moment(workflow?.votingDate).format('MMM DD, YYYY'),
        companyWorkflowCommentsLength : workflow?.companyWorkflowComments?.length,
        workflow: workflow
      };
    });
  }

  transformAlertsData(companies: any[]) {
    return companies.map((company) => {
      const formattedEmails = company?.recipients!
        .split(",") // Split by comma
        .map((email: any) => email.trim()) // Trim any extra spaces
        .join("\n"); // Join with new lines

      return {
        ...company,
        reciepientsList : formattedEmails,
        lastExecutionDate: company.lastExecutionDate ? this.getExecutionData(company.lastExecutionDate) : '-'
      };
    });
  }

  transformUsersData(companies: any[]) {
    return companies.map((company) => {
      const userRoles = this.getUserRoles(company.userRoles).map((item) => item).join("\n");
      const userPods = this.getUserPods(company.userGroups).map((item) => item).join("\n");
      const userGroups = this.getUserGroups(company.userGroups).map((item) => item).join("\n");
      return {
        ...company,
        userRolesList: userRoles,
        userPodsList: userPods,
        userGroupsList: userGroups,
      };
    });
  }

  getExecutionData(value: string): string {
    if (value === null) {
      return ''
    }
    const diff = this.dateDiff(value, new Date().toISOString());

    if (diff?.d !== 0) {

      return diff?.d && diff.d > 1 ? `${diff.d} Days ago` : `${diff?.d} Day${diff?.d === 1 ? '' : 's'} ago`;
    }

    if (diff?.h !== 0) {
      return diff?.h && diff.h > 1 ? `${diff.h} Hours ago` : `${diff?.h} Hour${diff?.h === 1 ? '' : 's'} ago`;
    }

    if (diff?.m !== 0) {
      return diff?.m && diff.m > 1 ? `${diff.m} Minutes ago` : `${diff?.m} Minute${diff?.m === 1 ? '' : 's'} ago`;
    }

    return 'Moments ago';
  }

  dateDiff(str1: string, str2: string): Nullable<DateDiff> {
    const diff = Date.parse(str2) - Date.parse(str1);
    return isNaN(diff) ? null : {
      diff: diff,
      ms: Math.floor(diff % 1000),
      s: Math.floor(diff / 1000 % 60),
      m: Math.floor(diff / 60000 % 60),
      h: Math.floor(diff / 3600000 % 24),
      d: Math.floor(diff / 86400000)
    };
  }

  getUserRoles(roles: any) {
    const roleIds = roles.map((role: any) => {
      return role.roleId;
    });
    const roleNames = this.userRolesService.userRoles
      .filter((userRole: any) => {
        return roleIds.includes(userRole.roleId);
      })
      .map((role) => role.name);
    return roleNames;
  }

  getUserPods(groups: any) {
    const groupIds = groups.map((group: any) => {
      return group.groupId;
    });
    const groupNames = this.userGroupsService.userGroups
      .filter((userGroup: any) => {
        return groupIds.includes(userGroup.id) && userGroup.groupType === 1;
      })
      .map((group) => {
        const sortedPodMembers = group.members.sort((a: any, b: any) => {
          return b.isAdmin - a.isAdmin;
        });
        group.name = "Pod: " + sortedPodMembers.map((member: any) => member.initials).join(", ");
        return group.name;
      });
    return groupNames;
  }

  getUserGroups(groups: any) {
    const groupIds = groups.map((group: any) => {
      return group.groupId;
    });
    const groupNames = this.userGroupsService.userGroups
      .filter((userGroup: any) => {
        return groupIds.includes(userGroup.id) && userGroup.groupType === 2;
      })
      .map((group) => group.name);
    return groupNames;
  }

  getVotingExportData(companyId?: number, startDate?: string, endDate?: string): Observable<ApiResponse<any>>{
    return this.post(`v2/companies/export/reviewed/archive`, null,
      {
        sortDirection: "desc",
        sortProperty: "votingDate",
        companyId: companyId,
        startDate: startDate,
        endDate: endDate,
      }
    )
  }
}
